/**
 * Table of Truth for CW notification conditions (excluding notifyWhenAttacksDecisive).
 * Source: backend evaluateNotificationConditions.ts
 *
 * | # | Notification Type            | Setting (enabled)                             | connectionStatus | Zeit-Fenster (remaining)                   | War-State / Spieler-Daten                                                                                   | My attacks |
 * | --| ---------------------------- | --------------------------------------------- | ---------------- | ------------------------------------------ | ----------------------------------------------------------------------------------------------------------- | --------- |
 * | 1 | 6h_before                    | notify6HoursBeforeEnd                         | beliebig         | 5.75h < remaining ≤ 6h                     | –                                                                                                           | irrelevant |
 * | 2 | 3h_before                    | notify3HoursBeforeEnd                         | beliebig         | 2.75h < remaining ≤ 3h                     | –                                                                                                           | irrelevant |
 * | 3 | 2h_before                    | notify2HoursBeforeEnd                         | beliebig         | 1.75h < remaining ≤ 2h                     | –                                                                                                           | irrelevant |
 * | 4 | 1h_before                    | notify1HourBeforeEnd                          | beliebig         | 45min < remaining ≤ 1h (minRemaining > 45) | –                                                                                                           | irrelevant |
 * | 5 | 30min_before                 | notify30MinBeforeEnd                          | beliebig         | 20min < remaining ≤ 30min                  | –                                                                                                           | irrelevant |
 * | 6 | 15min_before                 | notify15MinBeforeEnd                          | beliebig         | 10min < remaining ≤ 15min                  | –                                                                                                           | irrelevant |
 * | 7 | below_all_destroyed          | notifyWhenOpponentBasesBelowMyNumberDestroyed | connected        | beliebig                                   | Es gibt Positionen unter mir; alle Member mit mapPosition > myPosition haben bestOpponentAttack.stars === 3 | irrelevant |
 * | 8 | player_below_attacked        | notifyWhenPlayerBelowMeDidFirstAttack         | connected        | beliebig                                   | Spieler mit mapPosition === myPosition+1 hat attacks.length ≥ 1                                             | irrelevant |
 * | 9 | player_below_both_attacked   | notifyWhenPlayerBelowMeDidBothAttacks         | connected        | beliebig                                   | Spieler mit mapPosition === myPosition+1 hat attacks.length ≥ war.attacksPerMember                          | irrelevant |
 */

import type { CwNotificationType } from "@acme/db/schema";

export const CW_NOTIFICATION_TYPES_FOR_DEV_TEST: CwNotificationType[] = [
  "6h_before",
  "3h_before",
  "2h_before",
  "1h_before",
  "30min_before",
  "15min_before",
  "below_all_destroyed",
  "player_below_attacked",
  "player_below_both_attacked",
];

export const TIME_BASED_NOTIFICATION_TYPES: CwNotificationType[] = [
  "6h_before",
  "3h_before",
  "2h_before",
  "1h_before",
  "30min_before",
  "15min_before",
];

export const EVENT_BASED_NOTIFICATION_TYPES: CwNotificationType[] = [
  "below_all_destroyed",
  "player_below_attacked",
  "player_below_both_attacked",
];

/** Länge der komprimierten Simulation (24h Kampftag = 1,2 min). Für Fortschrittsbalken und Backend-Delays. */
export const TOTAL_SIMULATION_DURATION_MS = 1.2 * 60 * 1000;

/** Simulierte Kampfdauer (24h). Countdown zeigt diese Zeit, läuft aber mit SPEED_FACTOR schneller ab. */
export const SIMULATED_WAR_DURATION_MS = 24 * 60 * 60 * 1000;

/** 1 reale Sekunde = SPEED_FACTOR simulierte Sekunden. Für Anzeige: simulatedRemaining = SIMULATED_WAR_DURATION_MS - realElapsedMs * SPEED_FACTOR */
export const SIMULATION_SPEED_FACTOR = SIMULATED_WAR_DURATION_MS / TOTAL_SIMULATION_DURATION_MS;

/** 24h war (Kampftag) → 1,2 min test. Delays from test start (ms) and time-to-end to pass in war (ms) so backend sees the right window. */
export const TIME_CHECKPOINTS_24H_TO_2_4MIN: {
  type: CwNotificationType;
  delayMs: number;
  endTimeOffsetMs: number;
}[] = [
  { type: "6h_before", delayMs: 0.9 * 60 * 1000, endTimeOffsetMs: 5.9 * 60 * 60 * 1000 },
  { type: "3h_before", delayMs: 1.05 * 60 * 1000, endTimeOffsetMs: 2.875 * 60 * 60 * 1000 },
  { type: "2h_before", delayMs: 1.1 * 60 * 1000, endTimeOffsetMs: 1.875 * 60 * 60 * 1000 },
  { type: "1h_before", delayMs: 1.15 * 60 * 1000, endTimeOffsetMs: 52 * 60 * 1000 },
  { type: "30min_before", delayMs: 1.175 * 60 * 1000, endTimeOffsetMs: 25 * 60 * 1000 },
  { type: "15min_before", delayMs: 1.1875 * 60 * 1000, endTimeOffsetMs: 12.5 * 60 * 1000 },
];
